import app from 'flarum/forum/app';

// Shapes returned by the GraphIntegration backend proxying Zep Cloud.
export interface GraphNodeFromApi {
  created_at: string | null;
  name: string;
  summary: string;
  uuid: string;
  attributes: Record<string, unknown>;
  labels: string[];
  relevance: number;
  score: number;
}

export interface GraphEdgeFromApi {
  created_at: string | null;
  fact: string;
  name: string;
  source_node_uuid: string;
  target_node_uuid: string;
  uuid: string;
  attributes: Record<string, unknown>;
  episodes: string[];
  expired_at: string | null;
  invalid_at: string | null;
  relevance: number;
  score: number;
  valid_at: string | null;
}

// Normalized graph format used by the D3 renderer.
export interface GraphNode {
  id: string;
  label: string;
  raw: GraphNodeFromApi;
}

export interface GraphEdge {
  id: string;
  source: string;
  target: string;
  label: string;
  raw: GraphEdgeFromApi;
}

export interface GraphData {
  nodes: GraphNode[];
  edges: GraphEdge[];
}

interface GraphApiResponse {
  nodes: GraphNodeFromApi[];
  edges: GraphEdgeFromApi[];
}

function normalizeGraphResponse(response: GraphApiResponse): GraphData {
  const nodes: GraphNodeFromApi[] = response.nodes || [];
  const edges: GraphEdgeFromApi[] = response.edges || [];

  if (!nodes.length && !edges.length) {
    // eslint-disable-next-line no-console
    console.warn('[cip/graph-integration] No graph nodes/edges returned from backend');
  }

  const normalizedNodes: GraphNode[] = nodes.map((node) => ({
    id: node.uuid,
    label: node.name || node.summary || node.uuid,
    raw: node,
  }));

  const normalizedEdges: GraphEdge[] = edges.map((edge) => ({
    id: edge.uuid,
    source: edge.source_node_uuid,
    target: edge.target_node_uuid,
    label: edge.fact || edge.name || '',
    raw: edge,
  }));

  return {
    nodes: normalizedNodes,
    edges: normalizedEdges,
  };
}

export async function fetchGraph(): Promise<GraphData | null> {
  try {
    const apiUrl = app.forum.attribute('apiUrl') as string;

    const response: GraphApiResponse = await app.request({
      method: 'GET',
      url: apiUrl + '/graph-integration/graph',
    });

    return normalizeGraphResponse(response);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[cip/graph-integration] Error fetching graph from backend', error);
    return null;
  }
}

export async function fetchDiscussionGraph(discussionId: number): Promise<GraphData | null> {
  try {
    if (!discussionId || discussionId <= 0) {
      return {
        nodes: [],
        edges: [],
      };
    }

    const apiUrl = app.forum.attribute('apiUrl') as string;

    const response: GraphApiResponse = await app.request({
      method: 'GET',
      url: `${apiUrl}/graph-integration/graph`,
      params: {
        discussion_id: discussionId,
      },
    });

    return normalizeGraphResponse(response);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(
      '[cip/graph-integration] Error fetching discussion-scoped graph from backend',
      error
    );
    return null;
  }
}

